import { OsaNodeCoding } from "../../coding/models/OsaNodeCoding";
import { OsaWaySnapshot } from "../../coding/models/OsaWaySnapshot";
import { OsaComment } from "../../coding/models/OsaComment";
import {
  jwtAuthAxios,
  optionalAuthAxios,
} from "../../shared/utils/network-utils";
import { OptionalScope } from "../../shared/models/OptionalScope";
import { LifeCycleStage } from "../models/LifeCycleStage";
import {
  CreateOsaWay,
  OsaWay,
  UpdateOsaWay,
  DeviceOutputLinksResponse,
  GpxUploadLinksResponse,
  UploadInputType,
  UploadLinksRequest,
} from "../models/OsaWay";
import { MobileAppPhotoProcessing } from "../models/MobileAppPhotoProcessing";
import {
  OsaWayUploadStatus,
  OsaNode,
  UpdateOsaNodeLocation,
} from "../models/OsaNode";
import { compileAllPages, Page } from "../../shared/utils/http-utils";
import { CopyNodesRequest } from "../models/CopyNodesRequest";
import { LatestCodingChange } from "../../coding/models/LatestCodingChange";

const prefix = "/osa-ways";

export const OsaWayApi = {
  get: async (osaWayId: string, scopes: OptionalScope[] = []) =>
    await optionalAuthAxios().get<OsaWay>(`${prefix}/${osaWayId}`, {
      params: { scopes: scopes.join() },
    }),

  pageNodes: async (
    osaWayId: string,
    scopes: OptionalScope[],
    pageSize: number,
    pageNumber: number,
  ) =>
    await optionalAuthAxios().get<Page<OsaNode>>(
      `${prefix}/${osaWayId}/osa-nodes`,
      {
        params: {
          "page[size]": pageSize,
          "page[number]": pageNumber,
          scopes: scopes.join(),
        },
      },
    ),

  compileAllNodePages: async (
    osaWayId: string,
    scopes: OptionalScope[],
    pageSize: number,
  ) =>
    await compileAllPages<OsaNode>(
      async (pageNumber) =>
        await OsaWayApi.pageNodes(osaWayId, scopes, pageSize, pageNumber),
    ),

  listOsaWaySnapshots: async (osaWayId: string) =>
    await jwtAuthAxios().get<OsaWaySnapshot[]>(
      `${prefix}/${osaWayId}/all-snapshots`,
    ),

  create: async (request: CreateOsaWay, scopes: OptionalScope[] = []) =>
    await jwtAuthAxios().post<OsaWay>(`${prefix}?scopes=${scopes}`, request),

  update: async (
    osaWayId: string,
    way: UpdateOsaWay,
    scopes: OptionalScope[],
  ) =>
    await jwtAuthAxios().put<OsaWay>(
      `${prefix}/${osaWayId}?scopes=${scopes.join()}`,
      way,
    ),

  updateLifeCycleStage: async (
    osaWayId: string,
    lifeCycleStage: LifeCycleStage,
    scopes: OptionalScope[] = [],
  ) =>
    await jwtAuthAxios().put<OsaWay>(
      `${prefix}/${osaWayId}/life-cycle-stage?scopes=${scopes}`,
      {
        lifeCycleStage,
      },
    ),

  analyzeCoding: async (osaWayId: string) =>
    await jwtAuthAxios().post<void>(`${prefix}/${osaWayId}/analyze-coding`),

  getZipUploadLinks: async (osaWayId: string, body: UploadLinksRequest) =>
    await jwtAuthAxios().post<
      DeviceOutputLinksResponse | GpxUploadLinksResponse
    >(`${prefix}/${osaWayId}/upload-links`, body),

  delete: async (osaWayId: string) => {
    return await jwtAuthAxios().delete(`${prefix}/${osaWayId}`);
  },

  listCodingEntries: async (osaWayId: string) =>
    await optionalAuthAxios().get<OsaNodeCoding[]>(
      `${prefix}/${osaWayId}/all-coding`,
    ),

  getCodingCsv: async (osaWayId: string) => {
    return await jwtAuthAxios().get<Blob>(
      `${prefix}/csv?osaWayIds=${osaWayId}`,
      {
        responseType: "blob",
      },
    );
  },

  listComments: async (osaWayId: string) => {
    return await jwtAuthAxios().get<OsaComment[]>(
      `${prefix}/${osaWayId}/all-comments`,
    );
  },

  getPhotoUploadStatus: async (osaWayId: string) =>
    await jwtAuthAxios().get<MobileAppPhotoProcessing>(
      `${prefix}/${osaWayId}/photo-upload-status`,
    ),

  batchUpdateLocation: async (
    osaWayId: string,
    nodeLocationUpdate: UpdateOsaNodeLocation[],
  ) =>
    await jwtAuthAxios().put(
      `${prefix}/${osaWayId}/batch-update-location`,
      nodeLocationUpdate,
    ),

  startReadingUploadedFiles: async (
    osaWayId: string,
    inputType: UploadInputType,
  ) => {
    const url = `${prefix}/${osaWayId}/read-uploaded-files?inputType=${inputType}`;
    return await jwtAuthAxios().post<OsaWayUploadStatus>(url);
  },

  getUploadFilesStatus: async (osaWayId: string) => {
    const url = `${prefix}/${osaWayId}/read-uploaded-files/status`;
    return await jwtAuthAxios().get<OsaWayUploadStatus>(url);
  },

  compileNodes: async (osaWayId: string, scopes: OptionalScope[] = []) =>
    await jwtAuthAxios().post<OsaWay>(
      `${prefix}/${osaWayId}/compile-nodes?scopes=${scopes.join()}`,
    ),

  copyNodes: async (osaWayId: string, request: CopyNodesRequest) =>
    await jwtAuthAxios().post<string>(
      `${prefix}/${osaWayId}/copy-nodes`,
      request,
    ),

  getLatestCodingChange: async (osaWayId: string) =>
    await jwtAuthAxios().get<LatestCodingChange>(
      `${prefix}/${osaWayId}/latest-coding-change`,
    ),
};
